import React from 'react';

const CallModal = ({ 
  isOpen, 
  onClose, 
  loadingDetails, 
  conversationDetails, 
  formatPhoneNumber, 
  formatDuration 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-xl">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">Call Details</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl p-1"
          >
            ✕
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loadingDetails ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Loading conversation...</p>
            </div>
          ) : conversationDetails ? (
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="font-medium text-gray-600">From:</span> <span className="text-gray-900">{formatPhoneNumber(conversationDetails.from_number)}</span></div>
                  <div><span className="font-medium text-gray-600">To:</span> <span className="text-gray-900">{formatPhoneNumber(conversationDetails.to_number)}</span></div>
                  <div><span className="font-medium text-gray-600">Messages:</span> <span className="text-gray-900">{conversationDetails.turn_count}</span></div>
                  <div><span className="font-medium text-gray-600">Duration:</span> <span className="text-gray-900">{formatDuration(conversationDetails.turns?.[0]?.turn_timestamp, conversationDetails.turns?.[conversationDetails.turns.length - 1]?.turn_timestamp)}</span></div>
                </div>
                {(() => {
                  const smsCount = conversationDetails.turns?.filter(turn => turn.channel === 'sms').length || 0;
                  const voiceCount = conversationDetails.turns?.filter(turn => !turn.channel || turn.channel === 'voice').length || 0;
                  
                  if (smsCount > 0) {
                    return (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex gap-4 text-sm">
                          <span className="flex items-center gap-1 text-gray-600">
                            🎙️ <span className="font-medium">{voiceCount}</span> Voice
                          </span>
                          <span className="flex items-center gap-1 text-purple-600">
                            📱 <span className="font-medium">{smsCount}</span> SMS
                          </span>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
              
              {/* Transfer Information Section */}
              {conversationDetails.transfer_info && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-orange-600 text-lg">🔄</span>
                    <h4 className="font-semibold text-orange-800">Call Transfer</h4>
                  </div>
                  <div className="text-sm text-orange-700">
                    {conversationDetails.transfer_info.rule_name ? (
                      <p>This call was transferred due to: <span className="font-medium">{conversationDetails.transfer_info.rule_name}</span></p>
                    ) : (
                      <p>This call was transferred to a manager or department.</p>
                    )}
                    {conversationDetails.transfer_info.destination_name && (
                      <p className="mt-1">Transferred to: <span className="font-medium">{conversationDetails.transfer_info.destination_name}</span></p>
                    )}
                    {conversationDetails.transfer_info.destination_phone && (
                      <p className="mt-1 text-xs text-orange-600">Phone: {formatPhoneNumber(conversationDetails.transfer_info.destination_phone)}</p>
                    )}
                  </div>
                </div>
              )}
              
              <div>
                <h4 className="font-medium text-gray-900 mb-4">Transcript</h4>
                <div className="space-y-3">
                  {conversationDetails.turns
                    ?.filter(turn => turn.content?.role !== 'system' || turn.content?.event === 'transfer')
                    .sort((a, b) => (a.turn_timestamp || 0) - (b.turn_timestamp || 0))
                    .map((turn, index) => {
                      const isUser = turn.direction === 'user';
                      const isSystem = turn.direction === 'system';
                      const isStaff = turn.direction === 'staff';
                      const isSMS = turn.channel === 'sms';
                      const textContent = turn.content?.text || turn.content?.content || 'No text content';
                      const staffPhone = turn.content?.from;
                      
                      // System transfer message
                      if (isSystem && turn.content?.event === 'transfer') {
                        return (
                          <div key={index} className="flex justify-center">
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2 text-sm text-yellow-800 flex items-center gap-2">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                              <span className="font-medium">{textContent}</span>
                            </div>
                          </div>
                        );
                      }
                      
                      // Staff response message
                      if (isStaff) {
                        return (
                          <div key={index} className="flex justify-start">
                            <div className="max-w-[75%] rounded-2xl px-4 py-3 bg-green-50 border border-green-200 text-gray-900 rounded-bl-md">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-medium text-green-700">
                                  👤 Staff {staffPhone ? `(${formatPhoneNumber(staffPhone)})` : ''}
                                </span>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                  📱 SMS
                                </span>
                                <span className="text-xs text-gray-400">
                                  {new Date(turn.turn_timestamp).toLocaleTimeString('en-US', {
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    second: '2-digit'
                                  })}
                                </span>
                              </div>
                              <p className="text-sm leading-relaxed text-gray-900">
                                {textContent}
                              </p>
                            </div>
                          </div>
                        );
                      }
                      
                      return (
                        <div key={index} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                            isUser 
                              ? 'bg-blue-400 text-white rounded-br-md' 
                              : 'bg-gray-100 text-gray-900 rounded-bl-md'
                          }`}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-medium ${
                                isUser ? 'text-white' : 'text-gray-500'
                              }`}>
                                {isUser ? 'Caller' : 'Assistant'}
                              </span>
                              {isSMS && (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  isUser 
                                    ? 'bg-blue-400 text-blue-50' 
                                    : 'bg-purple-100 text-purple-700'
                                }`}>
                                  📱 SMS
                                </span>
                              )}
                              <span className={`text-xs ${
                                isUser ? 'text-white' : 'text-gray-400'
                              }`}>
                                {new Date(turn.turn_timestamp).toLocaleTimeString('en-US', {
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  second: '2-digit'
                                })}
                              </span>
                            </div>
                            <p className={`text-sm leading-relaxed ${
                              isUser ? 'text-white' : 'text-gray-900'
                            }`}>
                              {textContent}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-600">Failed to load conversation details.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallModal;